import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  Grid,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Event as EventIcon,
  Route as RouteIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

// Composant pour une ligne d'historique avec détails extensibles
function HistoryRow({ entry }) {
  const [open, setOpen] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={!entry.metadata}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDate(entry.createdAt)}</TableCell>
        <TableCell>
          <Chip label={entry.routePath} color="primary" size="small" />
        </TableCell>
        <TableCell>{entry.screenName || '-'}</TableCell>
        <TableCell>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
            {entry.sessionId?.substring(0, 12)}...
          </Typography>
        </TableCell>
      </TableRow>
      {entry.metadata && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Détails
                </Typography>
                <Grid container spacing={2}>
                  {entry.metadata.search && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Paramètres de recherche:</strong> {entry.metadata.search}
                      </Typography>
                    </Grid>
                  )}
                  {entry.metadata.hash && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Hash:</strong> {entry.metadata.hash}
                      </Typography>
                    </Grid>
                  )}
                  {entry.metadata.state && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>État:</strong> {JSON.stringify(entry.metadata.state)}
                      </Typography>
                    </Grid>
                  )}
                  {entry.userAgent && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>User Agent:</strong> {entry.userAgent}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Composant pour afficher les routes les plus visitées
function TopRoutesCard({ userId }) {
  const [topRoutes, setTopRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopRoutes = async () => {
      try {
        const data = await AdminService.getUserTopRoutes(userId, 10);
        setTopRoutes(data.topRoutes || []);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des routes populaires:', error);
        setLoading(false);
      }
    };

    fetchTopRoutes();
  }, [userId]);

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <RouteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Routes les plus visitées
        </Typography>
        <List dense>
          {topRoutes.map((route, index) => (
            <ListItem key={index}>
              <ListItemText
                primary={route.routePath}
                secondary={`${route.visitCount} visites`}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}

// Composant pour afficher les sessions
function SessionsDialog({ open, onClose, userId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    if (open) {
      fetchSessions();
    }
  }, [open, userId]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const data = await AdminService.getUserSessions(userId, 1, 50);
      setSessions(data.sessions || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des sessions:', error);
      setLoading(false);
    }
  };

  const handleViewSession = async (sessionId) => {
    setLoadingDetails(true);
    setSelectedSession(sessionId);
    try {
      const data = await AdminService.getUserSessionDetails(userId, sessionId);
      setSessionDetails(data.session || []);
      setLoadingDetails(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des détails de la session:', error);
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Sessions de navigation
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={selectedSession ? 6 : 12}>
              <List>
                {sessions.map((session) => (
                  <React.Fragment key={session.sessionId}>
                    <ListItem
                      button
                      onClick={() => handleViewSession(session.sessionId)}
                      selected={selectedSession === session.sessionId}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            Session du {formatDate(session.sessionStart)}
                          </Typography>
                        }
                        secondary={`${session.pageCount} pages vues`}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            </Grid>
            {selectedSession && (
              <Grid item xs={6}>
                <Typography variant="h6" gutterBottom>
                  Détails de la session
                </Typography>
                {loadingDetails ? (
                  <CircularProgress size={24} />
                ) : (
                  <List dense>
                    {sessionDetails.map((entry, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={entry.routePath}
                          secondary={formatDate(entry.createdAt)}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}

// Composant principal
export default function UserNavigationHistory() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [userId, page, rowsPerPage]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await AdminService.getUserNavigationHistory(
        userId,
        page + 1,
        rowsPerPage
      );
      setHistory(data.history || []);
      setTotalCount(data.pagination?.total || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching navigation history:', err);
      setError('Impossible de charger l\'historique de navigation');
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && history.length === 0) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error">{error}</Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Historique de navigation
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Utilisateur: {userId}
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TopRoutesCard userId={userId} />
        </Grid>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Historique complet ({totalCount} entrées)
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<EventIcon />}
                  onClick={() => setSessionsDialogOpen(true)}
                >
                  Voir par sessions
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="50px" />
                  <TableCell>Date / Heure</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Écran</TableCell>
                  <TableCell>Session</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((entry) => (
                  <HistoryRow key={entry.id} entry={entry} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page"
          />
        </CardContent>
      </Card>

      <SessionsDialog
        open={sessionsDialogOpen}
        onClose={() => setSessionsDialogOpen(false)}
        userId={userId}
      />
    </AdminLayout>
  );
}






